// The conversation as the user sees it: their turns, and each reply shown in full, lit word by
// word as it is spoken (karaoke from the TTS word timings).
//
// Replies are keyed by **process-unique reply ids**, scoped by the kit's `epoch`:
// - every brain message is routed by id, so an unprompted push never merges with, or shifts,
//   another reply, and an interrupted reply's late text lands in its own entry;
// - ids are never reused within one kit process; when the kit restarts (a new `welcome.epoch`)
//   the old entries keep their old key, so a new reply with the same number can't collide.
//   (live-voice-ai renumbered its entries on every session to get the same effect.)

import type { AttachmentInfo, Id, ServerMsg, SpeakerMatch, SpeakerSpan, Word } from "./protocol";

export interface UserEntry {
  role: "user";
  text: string;
  partial?: true;
  turnId?: Id;
  /**
   * The kit process (`welcome.epoch`) `turnId` belongs to: ids start over in each, so a turn is
   * known by both (as a reply is by its key). Absent on entries saved before it was kept.
   */
  epoch?: string;
  /** Files sent with the turn (protocol 1.5, `stt.final.attachments`). */
  attachments?: AttachmentInfo[];
  /**
   * The utterance split by voice (protocol 1.7, `stt.final.spans`): the user's words and, set
   * apart, another voice's that were ignored. Only in models made with `showIgnored`; `text` is
   * the user's words alone either way.
   */
  spans?: SpeakerSpan[];
  /**
   * 1.10: speech "only listen to me" had ignored, that the user marked "That was me" (its
   * `ignored_id`). Shown as theirs, in the ignored line's place.
   */
  mine?: string;
  /**
   * Kit 0.12: a marked line that was not sent to the assistant (it was not the latest thing the
   * user said): it can still be, with "Send to assistant" (`speech.mine.send`).
   */
  unsent?: true;
  /**
   * Kit 0.13: a marked line sent to the assistant late (`speech.mine.sent{late}`): after later
   * turns, or long after it was said. It stays where it was said; the assistant's answer comes
   * after the turns it already answered.
   */
  sentLate?: true;
}

export interface AssistantEntry {
  role: "assistant";
  /** `${epoch}:${replyId}`: unique for the life of the page. */
  key: string;
  replyId: Id;
  turnId: Id | null;
  text: string;
  /** Silenced (barge-in, tap to stop, a new turn, session end). Its text may keep coming. */
  interrupted: boolean;
  /** Never voiced (e.g. a push that expired while no session was running). */
  unspoken: boolean;
  /** An unprompted reply (the brain pushed it). */
  unprompted: boolean;
  /** A kit line ("Okay, cancelled.", "Bye!"). */
  canned: boolean;
  /** Generation ended (`reply.ended`): no more text will come. */
  ended?: boolean;
  error?: string;
}

/**
 * Speech the kit heard and ignored because it wasn't the user's voice ("only listen to me",
 * `speech.ignored{reason:"other_speaker"}`). Shown apart from the user's turns, never sent.
 * Only in models made with `showIgnored`.
 */
export interface IgnoredEntry {
  role: "ignored";
  text: string;
  reason: "other_speaker";
  /** The speaker match that failed: `score` against `threshold`. */
  speaker?: SpeakerMatch;
  /** When the page got it (`Date.now()`); the event itself has no timestamp. */
  at: number;
  /** 1.7: the turn this was the other voice's part of (`speech.ignored.turn_id`). */
  turnId?: Id;
  /** 1.8: the guest it was grouped into (`speech.ignored.guest_id`). */
  guestId?: string;
  /**
   * With `guestSpeech: "marker"`: a quiet "Guest 1 spoke" in place of the words (which are in
   * the guests panel). `count`: how many times in a row that guest spoke.
   */
  marker?: true;
  count?: number;
  /**
   * 1.10, `speech_mine`: the ignored speech's id(s), for "That was me" (`speech.mine`). A
   * marker that adds up several holds one per time.
   */
  ignoredIds?: string[];
}

export type Entry = UserEntry | AssistantEntry | IgnoredEntry;

export interface TranscriptOptions {
  /** Most entries kept (older ones scroll out of history). Default 100. */
  maxEntries?: number;
  /**
   * Show other voices' speech that "only listen to me" ignored, as `role: "ignored"` entries.
   * Off by default, so code that knows only user and assistant entries never sees one.
   */
  showIgnored?: boolean;
  /**
   * 1.8, once the kit groups other voices into guests: how their speech shows in the
   * conversation. `"inline"` (the default): their words, dimmed and labelled. `"marker"`: only a
   * quiet "David spoke", their words being in the guests panel ({@link GuestModel}). Without
   * guests, ignored speech always shows inline.
   */
  guestSpeech?: "inline" | "marker";
}

/** One spoken TTS chunk: words are `[text, startMs, endMs]` within the chunk. */
export interface SpokenChunk {
  text: string;
  ms: number;
  source: string | null;
  words: Word[];
}

/** One piece of a reply as shown, in reading order. */
export type SpeechPart =
  /** A spoken word; `at`: ms into this reply's audio when it starts. */
  | { kind: "word"; text: string; at: number }
  /** Spoken text not (yet) turned into speech. */
  | { kind: "pending"; text: string }
  /**
   * Text that is shown and never spoken (`reply.text` with `spoken:false`): display-only text
   * (protocol 1.3 `speak:false`), or text that arrived after the reply was silenced or marked
   * unspoken (`late`). Never karaoke.
   */
  | { kind: "plain"; text: string; late?: true };

/**
 * Where one assistant message ends and the next begins within one reply: U+2029 PARAGRAPH
 * SEPARATOR, sent shown-only (`ReplySink::show_only`), e.g. by a brain between its text before a
 * tool call and its text after it. The reply stays one reply (one id, one voice); views show
 * each message as its own bubble. Hosts that don't know it see a paragraph break.
 */
export const MESSAGE_BREAK = " ";

/**
 * A reply's parts as its messages, split at [`MESSAGE_BREAK`] (dropped). Display text at a
 * break is trimmed of whitespace, and a message with nothing to show is left out. Without a
 * break: one message, the parts as they are.
 */
export function splitMessages(parts: SpeechPart[]): SpeechPart[][] {
  const messages: SpeechPart[][] = [[]];
  for (const p of parts) {
    const pieces = p.kind === "word" ? [p.text] : p.text.split(MESSAGE_BREAK);
    pieces.forEach((text, i) => {
      if (i > 0) messages.push([]);
      if (text) messages[messages.length - 1].push({ ...p, text });
    });
  }
  if (messages.length === 1) return messages;
  const trimmed = messages.map((m, i) => {
    const out = [...m];
    // Words keep their text (each is a span lit as it is spoken).
    while (i > 0 && out.length && out[0].kind !== "word") {
      const text = out[0].text.trimStart();
      if (text) {
        out[0] = { ...out[0], text };
        break;
      }
      out.shift();
    }
    while (i < messages.length - 1 && out.length && out[out.length - 1].kind !== "word") {
      const text = out[out.length - 1].text.trimEnd();
      if (text) {
        out[out.length - 1] = { ...out[out.length - 1], text };
        break;
      }
      out.pop();
    }
    return out;
  });
  return trimmed.filter((m) => m.length > 0);
}

export interface SpeechView {
  /** `at`: ms into this reply's audio when the word starts. */
  words: Array<{ text: string; at: number }>;
  /** Reply text after the last spoken word (not yet, or never, turned into speech). */
  tail: string;
  /** The whole reply in reading order: spoken words, pending speech and display-only text. */
  parts: SpeechPart[];
  playedMs: number;
}

interface Speech {
  /** `pos`: where the word's chunk starts in `spokenText`. */
  words: Array<{ text: string; at: number; pos: number }>;
  /** The reply's shown text that is also spoken (`reply.text` with `spoken:true`). */
  spokenText: string;
  /** Shown-only text, anchored at the length `spokenText` had when it arrived. */
  plain: Array<{ pos: number; text: string; late: boolean }>;
  spokenMs: number;
  playedMs: number;
  rawCursor: number;
}

/** A reply's karaoke state as saved: its words (`at`: ms into its audio), text and cursor. */
export interface SavedSpeech {
  words: Array<{ text: string; at: number; pos: number }>;
  spokenText: string;
  plain: Array<{ pos: number; text: string; late: boolean }>;
  spokenMs: number;
  rawCursor: number;
}

/**
 * A saved transcript (`TranscriptModel.snapshot()`), plain JSON: what a page keeps so a reload
 * shows the conversation again straight away (the phone page: ./phone/history.ts).
 */
export interface TranscriptSnapshot {
  v: 1;
  epoch: string;
  entries: Entry[];
  /** Each reply's spoken words and shown-only text, by entry key (karaoke state, all lit). */
  speech: Record<string, SavedSpeech>;
  guestsActive?: boolean;
  guestLabels?: Record<string, string>;
}

export class TranscriptModel {
  entries: Entry[] = [];
  /** The kit process the ids belong to. */
  epoch = "";
  private readonly byKey = new Map<string, AssistantEntry>();
  private readonly speech = new Map<string, Speech>();
  /** Replies silenced before a word of them was heard: their text is never spoken. */
  private readonly silencedUnheard = new Set<string>();
  private readonly revs = new WeakMap<Entry, number>();
  private readonly listeners: Array<() => void> = [];

  private readonly maxEntries: number;
  private readonly showIgnored: boolean;
  private readonly guestSpeech: "inline" | "marker";
  /** 1.8: the kit groups other voices into guests (said in `welcome`, or a guest event came). */
  guestsActive = false;
  /** 1.8: each guest's label as the kit last gave it. */
  private readonly guestLabels = new Map<string, string>();

  /** `options` may also be the old `maxEntries` number. */
  constructor(options: TranscriptOptions | number = {}) {
    const o = typeof options === "number" ? { maxEntries: options } : options;
    this.maxEntries = o.maxEntries ?? 100;
    this.showIgnored = o.showIgnored ?? false;
    this.guestSpeech = o.guestSpeech ?? "inline";
  }

  /** Show other voices as markers (`guestSpeech: "marker"` and the kit has guests). */
  get guestMarkers(): boolean {
    return this.guestSpeech === "marker" && this.guestsActive;
  }

  /** A guest's label ("David", "Guest 2"), or "Someone". */
  guestLabel(id: string | undefined): string {
    return (id ? this.guestLabels.get(id) : undefined) ?? "Someone";
  }

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  /** Bumped whenever an entry's content changes, so views redraw only what changed. */
  revision(e: Entry): number {
    return this.revs.get(e) ?? 0;
  }

  /** The key a reply id has in the current kit process. */
  keyOf(replyId: Id): string {
    return `${this.epoch}:${replyId}`;
  }

  /** Feed every kit event here. */
  apply(m: ServerMsg): void {
    switch (m.type) {
      case "welcome":
        if (m.features?.includes("guests")) this.guestsActive = true;
        // A (re)connect: a reply that was still thinking will never finish here.
        this.settleThinking();
        // A new kit process keeps none of the old one's marked lines: nothing left to send.
        if (this.epoch && m.epoch !== this.epoch) this.clearUnsent();
        this.setEpoch(m.epoch);
        break;
      case "session.ended":
        this.settleThinking();
        break;
      case "guest.speech":
        this.guestsActive = true;
        if (m.guest_id) this.relabel(m.guest_id, m.label);
        break;
      case "guest.list":
        this.guestsActive = true;
        for (const g of m.guests) this.relabel(g.guest_id, g.label);
        break;
      case "stt.partial":
        if (m.text) this.userPartial(m.text);
        else this.clearPartial();
        break;
      case "stt.final":
        if (m.ignored_id) this.mine(m.ignored_id, m.text, m.turn_id, true);
        else this.user(m.text, m.turn_id, m.attachments, m.spans);
        break;
      case "speech.reattributed":
        this.mine(m.ignored_id, m.text, m.turn_id, false);
        if (m.sendable) this.unsent(m.ignored_id, true);
        break;
      case "speech.mine.sent":
        this.mine(m.ignored_id, undefined, m.turn_id, false);
        this.unsent(m.ignored_id, false);
        if (m.late) this.markSentLate(m.ignored_id);
        break;
      case "speech.ignored":
        if (this.showIgnored && m.reason === "other_speaker" && this.guestMarkers) {
          this.guestMarker(m.guest_id, m.speaker, Date.now(), m.turn_id, m.ignored_id);
        } else if (this.showIgnored && m.reason === "other_speaker" && m.text) {
          this.ignored(m.text, m.speaker, Date.now(), m.turn_id, m.guest_id, m.ignored_id);
        }
        else this.clearPartial();
        break;
      case "reply.started":
        this.started(m.reply_id, m.turn_id, m.unprompted, m.canned);
        break;
      case "reply.text":
        this.assistantDelta(m.reply_id, m.text, m.spoken);
        break;
      case "tts.chunk":
        this.ttsChunk(m.reply_id, { text: m.text, ms: m.ms, source: m.source, words: m.words });
        break;
      case "reply.heard":
        if (m.outcome === "silenced") this.interrupt(m.reply_id);
        else if (m.outcome === "finished") this.played(m.reply_id, Infinity);
        else this.markUnspoken(m.reply_id);
        break;
      case "reply.ended":
        if (m.outcome === "failed" && m.error) this.fail(m.reply_id, m.error);
        this.ended(m.reply_id);
        break;
      case "conversation.recent":
        this.catchUp(m.events);
        break;
    }
  }

  /**
   * What the conversation said while this page was away (`conversation.recent`, which a phone
   * gets right after `welcome`), merged into what is shown and never repeated: a turn already
   * shown (same `turn_id`) is skipped, a reply already shown gets only the text it is missing,
   * and anything new is added in order. Its text shows as it is, not lit word by word: it was
   * not voiced here.
   */
  catchUp(events: ServerMsg[]): void {
    for (const m of events) {
      switch (m.type) {
        case "stt.final":
          if (this.hasTurn(m.turn_id)) break;
          // 1.10: a turn marked "That was me" takes its ignored line's place, as live.
          if (m.ignored_id) {
            this.mine(m.ignored_id, m.text, m.turn_id, true);
            break;
          }
          this.removePartial();
          this.push({ role: "user", text: m.text, ...this.turnTag(m.turn_id), ...(m.attachments?.length ? { attachments: m.attachments } : {}) });
          this.changed();
          break;
        case "speech.reattributed":
          this.mine(m.ignored_id, m.text, m.turn_id, false);
          if (m.sendable) this.unsent(m.ignored_id, true);
          break;
        case "reply.started":
          if (!this.find(m.reply_id)) this.started(m.reply_id, m.turn_id, m.unprompted, m.canned);
          break;
        case "reply.text": {
          const shown = this.find(m.reply_id)?.text ?? "";
          if (m.text.length > shown.length && m.text.startsWith(shown)) this.assistantDelta(m.reply_id, m.text.slice(shown.length), false);
          break;
        }
        case "reply.ended":
        case "reply.heard":
          if (this.find(m.reply_id)) this.apply(m);
          break;
      }
    }
  }

  /** A turn of the current kit process is shown (turn ids start over in each). */
  private hasTurn(turnId: Id): boolean {
    return this.entries.some((e) => e.role === "user" && e.turnId === turnId && (e.epoch ?? "") === this.epoch);
  }

  /** A turn's id with its kit process (none before the first `welcome`). */
  private turnTag(turnId: Id | undefined): { turnId?: Id; epoch?: string } {
    if (turnId == null) return {};
    return this.epoch ? { turnId, epoch: this.epoch } : { turnId };
  }

  /** A new kit process: ids start over, so old entries are frozen under their old keys. */
  setEpoch(epoch: string): void {
    if (epoch === this.epoch) return;
    this.epoch = epoch;
    this.changed();
  }

  user(text: string, turnId?: Id, attachments?: AttachmentInfo[], spans?: SpeakerSpan[]): void {
    const last = this.entries[this.entries.length - 1];
    const split = this.showIgnored && spans?.some((s) => !s.mine) ? { spans } : {};
    const files = { ...(attachments?.length ? { attachments } : {}), ...split };
    // The other voice is in this turn's spans: drop a `speech.ignored` for the same turn that
    // came first (a server should send only one of the two; this keeps it shown once anyway).
    if (split.spans && turnId != null) this.dropIgnored(turnId);
    this.settleThinking(); // a newer turn: only its reply may show as thinking
    if (last?.role === "user" && last.partial) {
      last.text = text;
      delete last.partial;
      delete last.epoch;
      Object.assign(last, { turnId }, this.turnTag(turnId), files);
      this.touch(last);
    } else {
      // The partial may sit above a reply that started meanwhile: replace it there.
      this.removePartial();
      this.push({ role: "user", text, turnId, ...this.turnTag(turnId), ...files });
    }
    this.changed();
  }

  /** What the user is saying right now (replaced by user() when they finish). */
  userPartial(text: string): void {
    const last = this.entries[this.entries.length - 1];
    if (last?.role === "user" && last.partial) {
      last.text = text;
      this.touch(last);
    } else this.push({ role: "user", text, partial: true });
    this.changed();
  }

  /**
   * Another voice's speech, ignored: it takes the place of its live partial, if any. With a
   * `turnId` (the other voice's part of that turn) it is shown once: not again when the turn's
   * spans already show it, or when it is already shown.
   */
  ignored(text: string, speaker?: SpeakerMatch, at = Date.now(), turnId?: Id, guestId?: string, ignoredId?: string): void {
    if (turnId != null && this.shownForTurn(turnId)) {
      this.clearPartial();
      return;
    }
    this.removePartial();
    const e: IgnoredEntry = { role: "ignored", text, reason: "other_speaker", at };
    if (speaker) e.speaker = speaker;
    if (turnId != null) e.turnId = turnId;
    if (guestId) e.guestId = guestId;
    if (ignoredId) e.ignoredIds = [ignoredId];
    this.push(e);
    this.changed();
  }

  /**
   * 1.10, "That was me": the ignored speech `ignoredId` is the user's. Its line becomes theirs,
   * where it was: an ignored line or a one-time marker turns into the user's line, a marker that
   * adds up several loses one (the user's line goes just after it), and a split turn's other
   * voice joins the user's words. `fromTurn`: its `stt.final` (the turn it was delivered as),
   * shown as a new line only when the ignored line is not on screen (scrolled out, reloaded).
   */
  mine(ignoredId: string, text: string | undefined, turnId?: Id, fromTurn = false): void {
    const done = this.entries.find((e) => e.role === "user" && e.mine === ignoredId) as UserEntry | undefined;
    if (done) {
      if (turnId != null && done.turnId == null) Object.assign(done, this.turnTag(turnId));
      // Sent (its turn came): nothing left to send.
      if (turnId != null) delete done.unsent;
      if (text && !done.text) done.text = text;
      this.touch(done);
      this.changed();
      return;
    }
    const i = this.entries.findIndex(
      (e) => (e.role === "ignored" && !!e.ignoredIds?.includes(ignoredId)) || (e.role === "user" && !!e.spans?.some((s) => s.ignored_id === ignoredId)),
    );
    const found = i >= 0 ? this.entries[i] : undefined;
    const line = (words: string): UserEntry => ({ role: "user", text: words, ...this.turnTag(turnId), mine: ignoredId });
    if (found?.role === "user") {
      // A split turn: the other voice's part was the user too.
      found.spans = found.spans!.map((s) => (s.ignored_id === ignoredId ? { ...s, mine: true, guest_id: undefined } : s));
      found.mine = ignoredId;
      this.touch(found);
    } else if (found?.role === "ignored") {
      const words = text ?? (found.marker ? "" : found.text);
      if ((found.count ?? 1) > 1) {
        found.ignoredIds = found.ignoredIds!.filter((id) => id !== ignoredId);
        found.count = Math.max(1, (found.count ?? 1) - 1);
        this.touch(found);
        this.entries.splice(i + 1, 0, line(words));
      } else {
        this.entries[i] = line(words);
      }
    } else if (fromTurn && text) {
      this.removePartial();
      this.push(line(text));
    } else return;
    this.changed();
  }

  /**
   * Kit 0.12: the marked line `ignoredId` can still be sent to the assistant (`unsent`), or no
   * longer (it was sent).
   */
  unsent(ignoredId: string, unsent: boolean): void {
    const e = this.entries.find((x) => x.role === "user" && x.mine === ignoredId) as UserEntry | undefined;
    // From the event alone (`sendable`): a split turn's entry has the user's turn id already,
    // and its marked other-voice part can still be sent.
    if (!e || !!e.unsent === unsent) return;
    if (unsent) e.unsent = true;
    else delete e.unsent;
    this.touch(e);
    this.changed();
  }

  /** Kit 0.13: the marked line `ignoredId` was sent late; it keeps its place, marked so. */
  markSentLate(ignoredId: string): void {
    const e = this.entries.find((x) => x.role === "user" && x.mine === ignoredId) as UserEntry | undefined;
    if (!e || e.sentLate) return;
    e.sentLate = true;
    this.touch(e);
    this.changed();
  }

  /** Every marked line loses its "Send to assistant" (the kit that could send them is gone). */
  clearUnsent(): void {
    let any = false;
    for (const e of this.entries) {
      if (e.role === "user" && e.unsent) {
        delete e.unsent;
        this.touch(e);
        any = true;
      }
    }
    if (any) this.changed();
  }

  /**
   * 1.8, markers: someone else spoke (their words are in the guests panel). The same guest
   * speaking again right after adds to the last marker instead of stacking new ones.
   */
  guestMarker(guestId?: string, speaker?: SpeakerMatch, at = Date.now(), turnId?: Id, ignoredId?: string): void {
    if (turnId != null && this.shownForTurn(turnId)) {
      this.clearPartial();
      return;
    }
    this.removePartial();
    const last = this.entries[this.entries.length - 1];
    if (last?.role === "ignored" && last.marker && last.guestId === guestId && turnId == null && last.turnId == null) {
      last.count = (last.count ?? 1) + 1;
      last.at = at;
      if (speaker) last.speaker = speaker;
      if (ignoredId) last.ignoredIds = [...(last.ignoredIds ?? []), ignoredId];
      this.touch(last);
      this.changed();
      return;
    }
    const e: IgnoredEntry = { role: "ignored", text: "", reason: "other_speaker", at, marker: true, count: 1 };
    if (speaker) e.speaker = speaker;
    if (turnId != null) e.turnId = turnId;
    if (guestId) e.guestId = guestId;
    if (ignoredId) e.ignoredIds = [ignoredId];
    this.push(e);
    this.changed();
  }

  /** A guest's label changed (named, or recognised): entries that show it are redrawn. */
  private relabel(id: string, label: string): void {
    if (this.guestLabels.get(id) === label) return;
    this.guestLabels.set(id, label);
    let touched = false;
    for (const e of this.entries) {
      const shows = e.role === "ignored" ? e.guestId === id : e.role === "user" && !!e.spans?.some((s) => s.guest_id === id);
      if (shows) {
        this.touch(e);
        touched = true;
      }
    }
    if (touched) this.changed();
  }

  clearPartial(): void {
    if (this.removePartial()) this.changed();
  }

  started(replyId: Id, turnId: Id | null, unprompted: boolean, canned: boolean): void {
    const e = this.assistant(replyId);
    e.turnId = turnId;
    e.unprompted = unprompted;
    e.canned = canned;
    this.touch(e);
    this.changed();
  }

  /**
   * Reply text, routed by id only. An interrupted reply's text still lands in its own entry.
   * `spoken:false` text is shown as it is and never lit word by word.
   */
  assistantDelta(replyId: Id, text: string, spoken = true): void {
    const e = this.assistant(replyId);
    // Text of a reply silenced before any of it was heard (the user spoke while it was
    // thinking): it is never voiced, so it shows as not spoken, not as an interruption.
    if (!spoken && this.silencedUnheard.has(e.key)) e.unspoken = true;
    e.text += text;
    const sp = this.speechFor(e.key);
    if (spoken) sp.spokenText += text;
    else {
      const late = e.interrupted || e.unspoken;
      const last = sp.plain[sp.plain.length - 1];
      if (last && last.pos === sp.spokenText.length && last.late === late) last.text += text;
      else sp.plain.push({ pos: sp.spokenText.length, text, late });
    }
    this.touch(e);
    this.changed();
  }

  ttsChunk(replyId: Id, chunk: SpokenChunk): void {
    const e = this.assistant(replyId);
    if (e.interrupted) return;
    const sp = this.speechFor(e.key);
    let pos = sp.rawCursor;
    if (chunk.source) {
      const idx = sp.spokenText.indexOf(chunk.source, sp.rawCursor);
      if (idx < 0) {
        // Speech-only text (protocol 1.3 `show:false`): heard, never shown. Its audio still
        // takes time, so later words keep their place on the timeline.
        sp.spokenMs += chunk.ms;
        return;
      }
      pos = idx;
      sp.rawCursor = idx + chunk.source.length;
    }
    const last = sp.words[sp.words.length - 1];
    if (last && chunk.words.length && !/\s$/.test(last.text)) last.text += " "; // sentence boundary
    for (const [text, start] of chunk.words) sp.words.push({ text, at: sp.spokenMs + start, pos });
    sp.spokenMs += chunk.ms;
    this.touch(e);
    this.changed();
  }

  /** Playback position of a reply (ms of its audio heard so far). */
  played(replyId: Id, ms: number): void {
    const key = this.keyOf(replyId);
    if (!this.byKey.has(key)) return;
    // No change notification: views read positions every frame (see transcript-view.ts).
    this.speechFor(key).playedMs = ms;
  }

  /**
   * The reply's voice was silenced. Only a reply the user heard some of is "interrupted": one
   * with nothing to say yet (still thinking or in a tool, so talking was a mid-turn message)
   * keeps waiting for its text, and shown text none of which was spoken is marked unspoken.
   */
  interrupt(replyId: Id): void {
    const e = this.byKey.get(this.keyOf(replyId));
    if (!e || e.interrupted) return;
    if (!this.speech.get(e.key)?.words.length) {
      this.silencedUnheard.add(e.key);
      if (e.text) this.markUnspoken(replyId);
      return;
    }
    e.interrupted = true;
    this.touch(e);
    this.changed();
  }

  markUnspoken(replyId: Id): void {
    const e = this.byKey.get(this.keyOf(replyId));
    if (!e) return;
    e.unspoken = true;
    this.touch(e);
    this.changed();
  }

  /** Generation ended: an entry still without text stops showing as thinking. */
  ended(replyId: Id): void {
    const e = this.byKey.get(this.keyOf(replyId));
    if (!e || e.ended) return;
    e.ended = true;
    this.touch(e);
    this.changed();
  }

  /** Waiting for its first text: the brain is thinking or working in a tool. */
  isThinking(e: AssistantEntry): boolean {
    return !e.text && !e.ended && !e.unspoken && !e.error && !e.canned;
  }

  fail(replyId: Id, error: string): void {
    const e = this.assistant(replyId);
    e.error = error;
    this.touch(e);
    this.changed();
  }

  /** The reply with this id in the current kit process. */
  find(replyId: Id): AssistantEntry | undefined {
    return this.byKey.get(this.keyOf(replyId));
  }

  speechView(key: string): SpeechView | null {
    const entry = this.byKey.get(key);
    if (!entry) return null;
    const sp = this.speech.get(key);
    if (!sp) {
      const parts: SpeechPart[] = entry.text ? [{ kind: "pending", text: entry.text }] : [];
      return { words: [], tail: entry.text, parts, playedMs: 0 };
    }
    // Merge by position in the spoken text. At the same position, shown-only text came first
    // (it arrived while the spoken text was that long), then words, then pending speech.
    const items: Array<{ pos: number; rank: number; part: SpeechPart }> = [];
    for (const p of sp.plain) items.push({ pos: p.pos, rank: 0, part: p.late ? { kind: "plain", text: p.text, late: true } : { kind: "plain", text: p.text } });
    for (const w of sp.words) items.push({ pos: w.pos, rank: 1, part: { kind: "word", text: w.text, at: w.at } });
    const cuts = [...new Set(sp.plain.map((p) => p.pos).filter((p) => p > sp.rawCursor && p < sp.spokenText.length))].sort((a, b) => a - b);
    let from = sp.rawCursor;
    for (const to of [...cuts, sp.spokenText.length]) {
      if (to > from) items.push({ pos: from, rank: 2, part: { kind: "pending", text: sp.spokenText.slice(from, to) } });
      from = to;
    }
    items.sort((a, b) => a.pos - b.pos || a.rank - b.rank); // stable: arrival order within a rank
    const parts = items.map((i) => i.part);
    let lastWord = parts.length - 1;
    while (lastWord >= 0 && parts[lastWord].kind !== "word") lastWord--;
    return {
      words: sp.words.map((w) => ({ text: w.text, at: w.at })),
      tail: parts.slice(lastWord + 1).map((p) => p.text).join(""),
      parts,
      playedMs: sp.playedMs,
    };
  }

  /**
   * The conversation as plain JSON: the last `limit` entries, without a live partial. Files'
   * previews are kept only when small (`maxThumb` characters of data URL), else left out.
   */
  snapshot(limit = this.maxEntries, maxThumb = 24_000): TranscriptSnapshot {
    const entries: Entry[] = [];
    const speech: TranscriptSnapshot["speech"] = {};
    const shown = this.entries.filter((e) => !(e.role === "user" && e.partial) && !(e.role === "assistant" && !e.text && !e.error));
    for (const e of shown.slice(-limit)) {
      if (e.role === "user" && e.attachments?.length) {
        const attachments = e.attachments.map((a) => {
          const { thumb, ...rest } = a;
          return thumb && thumb.startsWith("data:image/") && thumb.length <= maxThumb ? { ...rest, thumb } : rest;
        });
        entries.push({ ...e, attachments });
      } else entries.push({ ...e });
      if (e.role === "assistant") {
        const sp = this.speech.get(e.key);
        if (sp) speech[e.key] = { words: sp.words, spokenText: sp.spokenText, plain: sp.plain, spokenMs: sp.spokenMs, rawCursor: sp.rawCursor };
      }
    }
    const snap: TranscriptSnapshot = { v: 1, epoch: this.epoch, entries, speech };
    if (this.guestsActive) snap.guestsActive = true;
    if (this.guestLabels.size) snap.guestLabels = Object.fromEntries(this.guestLabels);
    return snap;
  }

  /**
   * Shows a saved conversation again (after a page reload), replacing what is shown. Replies
   * come back fully lit; one still without text stops showing as thinking. Replies of the same
   * kit process (`welcome.epoch`) keep their keys, so their late text still lands in them.
   */
  restore(snap: TranscriptSnapshot | null | undefined): boolean {
    if (!snap || snap.v !== 1 || !Array.isArray(snap.entries)) return false;
    this.entries = [];
    this.byKey.clear();
    this.speech.clear();
    this.silencedUnheard.clear();
    if (typeof snap.epoch === "string") this.epoch = snap.epoch;
    if (snap.guestsActive) this.guestsActive = true;
    for (const [id, label] of Object.entries(snap.guestLabels ?? {})) this.guestLabels.set(id, label);
    for (const raw of snap.entries.slice(-this.maxEntries)) {
      if (!raw || typeof raw !== "object") continue;
      if (raw.role === "user") {
        if (raw.partial || typeof raw.text !== "string") continue;
        this.entries.push({ ...raw });
      } else if (raw.role === "ignored") {
        if (typeof raw.text === "string") this.entries.push({ ...raw });
      } else if (raw.role === "assistant" && typeof raw.key === "string" && typeof raw.text === "string") {
        const e: AssistantEntry = { ...raw };
        if (!e.text && !e.error) continue; // never a thinking-only bubble from history
        this.byKey.set(e.key, e);
        const sp = snap.speech?.[e.key];
        if (sp && Array.isArray(sp.words)) this.speech.set(e.key, { ...sp, plain: sp.plain ?? [], playedMs: Infinity });
        this.entries.push(e);
      }
    }
    this.changed();
    return true;
  }

  clear(): void {
    this.entries = [];
    this.byKey.clear();
    this.speech.clear();
    this.silencedUnheard.clear();
    this.changed();
  }

  private speechFor(key: string): Speech {
    let sp = this.speech.get(key);
    if (!sp) this.speech.set(key, (sp = { words: [], spokenText: "", plain: [], spokenMs: 0, playedMs: 0, rawCursor: 0 }));
    return sp;
  }

  /**
   * Earlier replies still waiting for their first text stop showing as thinking: only the
   * current in-flight reply does. Their text still shows if it comes after all.
   */
  settleThinking(except?: string): void {
    let any = false;
    for (const e of this.entries) {
      if (e.role === "assistant" && e.key !== except && this.isThinking(e)) {
        e.ended = true;
        this.touch(e);
        any = true;
      }
    }
    if (any) this.changed();
  }

  private assistant(replyId: Id): AssistantEntry {
    const key = this.keyOf(replyId);
    const found = this.byKey.get(key);
    if (found) return found;
    this.settleThinking(key); // a newer reply: earlier ones no longer think
    const entry: AssistantEntry = {
      role: "assistant",
      key,
      replyId,
      turnId: null,
      text: "",
      interrupted: false,
      unspoken: false,
      unprompted: false,
      canned: false,
    };
    this.byKey.set(key, entry);
    this.push(entry);
    return entry;
  }

  /** The other voice's part of this turn is shown already (in its spans or its own entry). */
  private shownForTurn(turnId: Id): boolean {
    return this.entries.some((e) =>
      e.role === "user" ? e.turnId === turnId && !!e.spans?.some((s) => !s.mine) : e.role === "ignored" && e.turnId === turnId,
    );
  }

  private dropIgnored(turnId: Id): void {
    this.entries = this.entries.filter((e) => !(e.role === "ignored" && e.turnId === turnId));
  }

  private removePartial(): boolean {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const e = this.entries[i];
      if (e.role === "user") {
        if (!e.partial) return false;
        this.entries.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  private push(e: Entry): void {
    this.entries.push(e);
    while (this.entries.length > this.maxEntries) {
      const old = this.entries.shift()!;
      if (old.role === "assistant") {
        this.byKey.delete(old.key); // frees word timings of replies scrolled out of history
        this.speech.delete(old.key);
        this.silencedUnheard.delete(old.key);
      }
    }
  }

  private touch(e: Entry): void {
    this.revs.set(e, (this.revs.get(e) ?? 0) + 1);
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}
